import { faPencilAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CommonErrorMsg } from 'aa_common/front-end/antd'
import { If } from 'aa_common/front-end/components'
import { everyTrue, ifElse } from 'aa_common/front-end/helpers'
import { DatePicker as DatePickerAnt } from 'antd'
import { DATE_FORMAT, DATE_FORMAT_RFC } from 'common/constants'
import { getIn, useFormikContext } from 'formik'
import i18n from 'i18n'
import get from 'lodash/get'
import moment, { Moment } from 'moment'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { Pending, Wrapper } from './styles'
import { useActionHandlers } from './use-action-handlers'

type Props = {
  name: string
  placeholder?: string
  dateFormat?: string
  style?: React.CSSProperties
  unsetable?: boolean
  allowClear?: boolean
  minDateField?: string
  maxDateField?: string
  wrapperStyle?: string
  disabled?: boolean
  afterChanged?: (value: Moment | null) => void
  className?: string
}

const FULL_YEAR_MONTH_DATE_FORMAT = 'YYYYMMDD'
const YEAR_MONTH_DATE_FORMAT = 'YYMMDD'
const MONTH_DATE_FORMAT = 'MMDD'
const DAY_FORMAT = 'DD'

const DatePicker: React.FC<Props> = ({
  name,
  placeholder,
  dateFormat = DATE_FORMAT,
  unsetable = false,
  style,
  allowClear = true,
  minDateField = null,
  maxDateField = null,
  disabled = false,
  wrapperStyle = '',
  afterChanged,
  className,
}) => {
  const { errors, values, setFieldValue } = useFormikContext()
  const [value, setValue] = useState<any>(null)
  const refContainer = useRef(null)
  const error = get(errors, name)
  const minDateValue = minDateField ? get(values, minDateField) : null
  const maxDateValue = maxDateField ? get(values, maxDateField) : null
  const fieldValue = getIn(values, name)

  const { disabledDate, handleEdit, handleChanged } = useActionHandlers(
    name,
    minDateValue,
    maxDateValue,
    setValue,
    setFieldValue,
    afterChanged
  )

  useEffect(() => {
    if (fieldValue !== value) {
      if (everyTrue(fieldValue, typeof fieldValue !== 'string')) {
        setFieldValue(name, fieldValue.format(DATE_FORMAT_RFC))
      }

      setValue(fieldValue)
    }
  }, [fieldValue]) // eslint-disable-line

  return useMemo(() => {
    return (
      <Wrapper ref={refContainer} className={wrapperStyle}>
        <If condition={!unsetable || everyTrue(unsetable, value)}>
          <DatePickerAnt
            format={[dateFormat, FULL_YEAR_MONTH_DATE_FORMAT, YEAR_MONTH_DATE_FORMAT, MONTH_DATE_FORMAT, DAY_FORMAT]}
            placeholder={placeholder}
            onChange={handleChanged}
            value={ifElse(typeof value === 'string', moment(value), value)}
            className={className || ifElse(error, 'error', undefined)}
            style={style}
            allowClear={allowClear}
            disabledDate={minDateField || maxDateField ? disabledDate : undefined}
            disabled={disabled}
            getPopupContainer={() => refContainer.current as any}
          />
        </If>

        <If condition={everyTrue(unsetable, !value)}>
          <Pending>
            {i18n.t('common.form_values.unset')}
            <FontAwesomeIcon icon={faPencilAlt} className="edit-icon" onClick={handleEdit} />
          </Pending>
        </If>

        <If condition={error}>
          <CommonErrorMsg>{error}</CommonErrorMsg>
        </If>
      </Wrapper>
    )
  }, [value, error, minDateValue, maxDateValue, handleChanged]) // eslint-disable-line
}

export default DatePicker
